/**
 * OPL2 Audio Protocol
 *
 * Defines message types for streaming OPL2-style FM synthesis data
 * between the stream server and client synthesizer.
 */

// =============================================================================
// OPL Voice Data
// =============================================================================

/**
 * OPL2 waveform types (matches real OPL2 hardware)
 */
export enum OPLWaveform {
  SINE = 0,        // Full sine wave
  HALF_SINE = 1,   // Positive half only
  ABS_SINE = 2,    // Absolute value (rectified)
  PULSE_SINE = 3,  // Quarter sine, then zero
}

/**
 * Single OPL voice state
 */
export interface OPLVoiceData {
  /** Voice ID (0-7) */
  id: number;

  /** Frequency in Hz (quantized to OPL2 range ~65-2100 Hz) */
  freq: number;

  /** Amplitude (0.0-1.0) */
  amp: number;

  /** Modulation frequency ratio (1-16) */
  modRatio: number;

  /** Modulation depth/index (0.0-4.0) */
  modDepth: number;

  /** Waveform type for carrier */
  waveform: OPLWaveform;

  /** Attack time in ms */
  attack: number;

  /** Decay time in ms */
  decay: number;

  /** Sustain level (0.0-1.0) */
  sustain: number;

  /** Release time in ms */
  release: number;

  /** Note on/off state */
  noteOn: boolean;
}

/**
 * Complete OPL state update (all 8 voices)
 */
export interface OPLStateMessage {
  type: 'opl:state';
  timestamp: number;
  voices: OPLVoiceData[];
}

/**
 * Single voice update (for efficiency when only one voice changes)
 */
export interface OPLVoiceMessage {
  type: 'opl:voice';
  voice: OPLVoiceData;
}

/**
 * Enable/disable OPL audio
 */
export interface OPLModeMessage {
  type: 'opl:mode';
  enabled: boolean;
}

/**
 * All OPL message types
 */
export type OPLMessage = OPLStateMessage | OPLVoiceMessage | OPLModeMessage;

// =============================================================================
// OPL2 Constants
// =============================================================================

/** Number of simultaneous voices (like OPL2's 9, but we use 8 for simplicity) */
export const OPL_NUM_VOICES = 8;

/** Minimum frequency (C2 ~65 Hz) */
export const OPL_MIN_FREQ = 65;

/** Maximum frequency (C7 ~2093 Hz) */
export const OPL_MAX_FREQ = 2100;

/** Sample rate for audio analysis */
export const OPL_SAMPLE_RATE = 22050;

/** FFT window size (2048 samples = ~93ms at 22050Hz) */
export const OPL_FFT_SIZE = 2048;

/**
 * OPL2 frequency table (chromatic scale from C2 to C7)
 * These are the "valid" frequencies that the OPL2 can produce
 */
export const OPL_FREQUENCY_TABLE: number[] = [];

// Generate chromatic scale from C2 (~65 Hz) to C7 (~2093 Hz)
// Using equal temperament: f = 65.41 * 2^(n/12) where n = 0..60
const C2_FREQ = 65.41;
for (let n = 0; n <= 60; n++) {
  OPL_FREQUENCY_TABLE.push(C2_FREQ * Math.pow(2, n / 12));
}

/**
 * Quantize a frequency to the nearest OPL2 frequency
 */
export function quantizeToOPLFrequency(freq: number): number {
  if (freq < OPL_MIN_FREQ) return OPL_FREQUENCY_TABLE[0];
  if (freq > OPL_MAX_FREQ) return OPL_FREQUENCY_TABLE[OPL_FREQUENCY_TABLE.length - 1];

  // Find closest frequency in table
  let closest = OPL_FREQUENCY_TABLE[0];
  let minDiff = Math.abs(freq - closest);

  for (const tableFreq of OPL_FREQUENCY_TABLE) {
    const diff = Math.abs(freq - tableFreq);
    if (diff < minDiff) {
      minDiff = diff;
      closest = tableFreq;
    }
  }

  return closest;
}

// =============================================================================
// Binary Encoding (for efficient WebSocket transmission)
// =============================================================================

/**
 * Encode OPL state to binary buffer
 * Format: [timestamp:4][numVoices:1][voice0...voiceN]
 * Voice: [id:1][freq:2][amp:1][modRatio:1][modDepth:1][waveform:1][adsr:4][noteOn:1]
 */
export function encodeOPLState(voices: OPLVoiceData[], timestamp: number): Buffer {
  const voiceSize = 12; // bytes per voice: id(1)+freq(2)+amp(1)+mod(2)+wave(1)+adsr(4)+noteOn(1)
  const buffer = Buffer.alloc(5 + voices.length * voiceSize);

  let offset = 0;

  // Header - use lower 32 bits of timestamp (wraps every ~49 days, fine for sync)
  buffer.writeUInt32LE(timestamp % 0x100000000, offset);
  offset += 4;
  buffer.writeUInt8(voices.length, offset);
  offset += 1;

  // Voices
  for (const voice of voices) {
    buffer.writeUInt8(voice.id, offset);
    offset += 1;

    // Frequency: 16-bit (0-65535 maps to Hz)
    buffer.writeUInt16LE(Math.min(65535, Math.max(0, Math.round(voice.freq))), offset);
    offset += 2;

    // Amplitude: 8-bit (0-255 maps to 0.0-1.0)
    buffer.writeUInt8(Math.min(255, Math.max(0, Math.round(voice.amp * 255))), offset);
    offset += 1;

    // Mod ratio: 8-bit (1-16)
    buffer.writeUInt8(Math.min(255, Math.max(0, Math.round(voice.modRatio))), offset);
    offset += 1;

    // Mod depth: 8-bit (0-255 maps to 0.0-4.0)
    // Clamp modDepth to 0-4.0 range before encoding
    const clampedModDepth = Math.min(4.0, Math.max(0, voice.modDepth));
    buffer.writeUInt8(Math.min(255, Math.round(clampedModDepth * 63.75)), offset);
    offset += 1;

    // Waveform: 8-bit (0-3)
    buffer.writeUInt8(Math.min(3, Math.max(0, voice.waveform)), offset);
    offset += 1;

    // ADSR: 4 bytes (attack, decay, sustain, release)
    // Each is 8-bit scaled (0-255 maps to 0-1000ms for A/D/R, 0-1.0 for S)
    buffer.writeUInt8(Math.min(255, Math.max(0, Math.round(voice.attack / 4))), offset);
    offset += 1;
    buffer.writeUInt8(Math.min(255, Math.max(0, Math.round(voice.decay / 4))), offset);
    offset += 1;
    buffer.writeUInt8(Math.min(255, Math.max(0, Math.round(voice.sustain * 255))), offset);
    offset += 1;
    buffer.writeUInt8(Math.min(255, Math.max(0, Math.round(voice.release / 4))), offset);
    offset += 1;

    // Note on: 1 byte
    buffer.writeUInt8(voice.noteOn ? 1 : 0, offset);
    offset += 1;
  }

  return buffer;
}

/**
 * Decode OPL state from binary buffer
 */
export function decodeOPLState(buffer: ArrayBuffer): { timestamp: number; voices: OPLVoiceData[] } {
  const view = new DataView(buffer);
  let offset = 0;

  // Header
  const timestamp = view.getUint32(offset, true);
  offset += 4;
  const numVoices = view.getUint8(offset);
  offset += 1;

  // Voices
  const voices: OPLVoiceData[] = [];
  for (let i = 0; i < numVoices; i++) {
    const voice: OPLVoiceData = {
      id: view.getUint8(offset),
      freq: view.getUint16(offset + 1, true),
      amp: view.getUint8(offset + 3) / 255,
      modRatio: view.getUint8(offset + 4),
      modDepth: view.getUint8(offset + 5) / 63.75,
      waveform: view.getUint8(offset + 6),
      attack: view.getUint8(offset + 7) * 4,
      decay: view.getUint8(offset + 8) * 4,
      sustain: view.getUint8(offset + 9) / 255,
      release: view.getUint8(offset + 10) * 4,
      noteOn: view.getUint8(offset + 11) !== 0,
    };
    voices.push(voice);
    offset += 12;
  }

  return { timestamp, voices };
}

/**
 * Create a default (silent) voice
 */
export function createDefaultVoice(id: number): OPLVoiceData {
  return {
    id,
    freq: 440,
    amp: 0,
    modRatio: 2,
    modDepth: 1.0,
    waveform: OPLWaveform.SINE,
    attack: 10,
    decay: 100,
    sustain: 0.7,
    release: 200,
    noteOn: false,
  };
}

/**
 * Create array of default voices
 */
export function createDefaultVoices(): OPLVoiceData[] {
  return Array.from({ length: OPL_NUM_VOICES }, (_, i) => createDefaultVoice(i));
}
